import { useState } from 'react';
import imageCompression from 'browser-image-compression';
import { auth } from '../../../firebase'; // 🎯 [Fix] Admin fallback


// Configuration
const WORKER_URL = "https://spot-media-handler.pycman21.workers.dev"; // ✅ Cloudflare Worker URL
// const MAX_RETRIES = 3;

/**
 * useImageUpload Hook
 * Handles compression and secure upload to R2 via Worker
 */
export const useImageUpload = () => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);

    /**
     * Compress Image based on Type
     */
    const compressImage = async (file, type) => {
        // 🎯 [GIF Support] Skip compression for GIFs to preserve animation
        if (file.type === 'image/gif') {
            console.log('[ImageUpload] Skipping compression for GIF:', file.name);
            return file;
        }

        // Policy: Store/Banner/City NFT (3MB), Post (1MB)
        const isLargeType = type === 'store' || type === 'banner' || type === 'city_nft';
        const maxSizeMB = isLargeType ? 3.0 : 1.0;
        const maxWidthOrHeight = isLargeType ? 1920 : 1500; // 🎯 [Fix] Correct variable reference

        const options = {
            maxSizeMB: maxSizeMB,
            maxWidthOrHeight: maxWidthOrHeight, // Optimized resolution
            useWebWorker: true,
            initialQuality: 0.7 // Reduced slightly for speed
        };

        try {
            const compressedFile = await imageCompression(file, options);
            console.log(`[ImageUpload] Compressed (${type}): ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
            return compressedFile;
        } catch (error) {
            console.error("[ImageUpload] Compression failed:", error);
            throw new Error("COMPRESSION_FAILED");
        }
    };

    /**
     * Generate Thumbnail
     */
    const generateThumbnail = async (file) => {
        // 🎯 [GIF Support] Skip thumbnail for GIFs
        if (file.type === 'image/gif') {
            return null;
        }

        const options = {
            maxSizeMB: 0.05, // 50KB target
            maxWidthOrHeight: 200, // Thumbnail size
            useWebWorker: true,
            initialQuality: 0.7
        };

        try {
            const compressedFile = await imageCompression(file, options);
            console.log(`[ImageUpload] Thumbnail: ${(compressedFile.size / 1024).toFixed(2)}KB`);
            return compressedFile;
        } catch (error) {
            console.warn("[ImageUpload] Thumbnail generation failed:", error);
            return null; // Fail silently, just no thumbnail
        }
    };

    /**
     * Upload Single File (Main + Thumbnail)
     */
    const uploadFile = async (file, type, piUser, envName = 'prod') => {
        if (!file) return null;

        // 🎯 [검증] piUser 필수 체크 (Admin 모드 등 예외 허용을 위해 strict check 제거)
        if (!piUser?.uid) {
            console.warn("[ImageUpload] No piUser provided. Trying upload without auth header.");
            // throw new Error("LOGIN_REQUIRED");
        }

        // 1. Compress Main Image
        const compressedFile = await compressImage(file, type);

        // 2. Generate Thumbnail (Parallel safe)
        const thumbnailFile = await generateThumbnail(file);

        // Simple auth header
        const currentUser = auth.currentUser;
        const authHeader = piUser?.uid
            ? `Bearer ${piUser.uid}`
            : currentUser
                ? `Bearer ${currentUser.uid}`
                : '';

        // Helper to upload one blob
        const uploadCommit = async (blob, filenameSuffix = '') => {
            const res = await fetch(`${WORKER_URL}/put-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': authHeader },
                body: JSON.stringify({
                    filename: (filenameSuffix ? 'thumb_' : '') + file.name,
                    contentType: file.type,
                    contentLength: blob.size,
                    type: type,
                    env: envName // 🎯 [NEW] 환경 구분 전송
                })
            });

            if (!res.ok) throw new Error("UPLOAD_INIT_FAILED");
            const { url, publicUrl } = await res.json();

            const putRes = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: blob
            });

            if (!putRes.ok) throw new Error("R2_UPLOAD_FAILED");
            return publicUrl;
        };

        try {
            // 3. Upload Both Parallely
            const uploadPromise = uploadCommit(compressedFile);
            const thumbPromise = thumbnailFile ? uploadCommit(thumbnailFile, 'thumb') : Promise.resolve(null);

            const [mainUrl, thumbUrl] = await Promise.all([uploadPromise, thumbPromise]);

            // Return Object { url, thumbnailUrl }
            return { url: mainUrl, thumbnailUrl: thumbUrl };
        } catch (err) {
            console.error("[ImageUpload] Upload failed:", err);
            throw err;
        }
    };

    /**
     * Upload Multiple Files (Parallel Optimized)
     */
    const uploadImages = async (files, type, piUser, envName = 'prod') => {
        setUploading(true);
        setProgress(0);
        setError(null);

        // 🎯 [Parallel] Speed Boost: Upload all images concurrently
        try {
            let completed = 0;
            const uploadPromises = files.map(async (file) => {
                const result = await uploadFile(file, type, piUser, envName);
                completed++;
                setProgress(Math.round((completed / files.length) * 100)); // Rough progress
                return result;
            });

            const urls = await Promise.all(uploadPromises);
            return urls;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setUploading(false);
        }
    };

    return {
        uploadImages,
        uploading,
        progress,
        error
    };
};
